import * as React from 'react'
import { Box, Button, Icon, Tooltip, styled } from '@design/pt-ui'
import DI from '../../app/di'
import { IProjectService } from '../../interfaces/services/projects'

import ProjectDialog from '../../components/ProjectDialog'
import SomethingIcon from '../../components/SomethingIcon'
import {
  Project,
  ProjectAction,
  UnsavedProjectType,
} from '../../interfaces/entities/project'
import { Optionset } from '../../interfaces/components/options'
import CallToAction from '../../components/CallToAction'
import { Descriptive } from '../../interfaces/base'
import { translateWithUserLocale } from '../../utils/i18n'
import excellFileIcon from '../../assets/images/excell-file-icon.png'
import { utils, writeFile } from 'xlsx-js-style'

const ActionIcon = styled(Icon)(({ theme }) => ({
  fontSize: '18px !important',
  color: theme.palette.custom.gray.dark,
}))

const IconContainer = styled('div')({
  width: 18,
  height: 18,
})

const getActionMeta = (action: ProjectAction) => {
  if (action.name === 'reopen') {
    return {
      confirm: true,
      dialog: {
        message:
          'While reopening the project, do you also want to reopen all previously unsent creatives?',
        options: [
          {
            label: 'Reopen project and unsent creatives',
            value: 'true',
          },
          { label: 'Reopen project only', value: 'false' },
        ],
      },
    }
  }

  if (action.name === 'delete' || 'archive') {
    return {
      confirm: true,
      dialog: {
        title: translateWithUserLocale('projects.confirmation_dialog_title'),
        message:
          action.name === 'delete'
            ? translateWithUserLocale(
                'projects.confirmation_dialog_message_delete',
              )
            : translateWithUserLocale(
                'projects.confirmation_dialog_message_archive',
              ),
      },
    }
  }

  return {
    confirm: false,
  }
}

interface IActions {
  project: Project
  onProjectSave: (project: Project | UnsavedProjectType) => void
  onActionClick: (
    action: Descriptive,
    project: Project,
    option?: string,
  ) => void
  onFavoriteChange: () => void
  canCopy: boolean
  openInvalidCopyProjectDialog: (
    invalidFormatsetsText: string,
    copiedProjectId: string,
  ) => void
}

type JsonArrayItemType = {
  Platform: string
  Project: string
  Formatset: string
  Creative: string
  Previewlink: string
  'Expiration Time': string
}

const actionIcons: Optionset = [
  {
    edit: 'edit',
    view: 'visibility',
    archive: 'archive',
    delete: 'delete',
    reopen: 'repeat',
    send_to_agency: 'send',
    download: 'download',
  },
  'build',
]

interface IActionButtonProps {
  action: ProjectAction
  onClick: (action: Descriptive, option?: string) => any
}

const ActionButton = (props: IActionButtonProps) => {
  const handleClick = (option?: string) => {
    props.onClick(props.action, option)
  }

  const meta = getActionMeta(props.action)
  return (
    <CallToAction
      onClick={handleClick}
      confirm={meta.confirm}
      dialog={meta.dialog}>
      <Button>
        <SomethingIcon
          id={props.action.name}
          icons={actionIcons}
          sx={{ fontSize: '18px !important', color: 'custom.gray.dark' }}
        />
      </Button>
    </CallToAction>
  )
}

function Actions(props: IActions) {
  const handleAction = (action: Descriptive, option?: string) => {
    props.onActionClick(action, props.project, option)
  }

  const handleViewAction = () => {
    const history = DI.get('history')
    history.push(`/project/${props.project.id}`)
  }

  const getProjectService = () => {
    return DI.get('ProjectService') as IProjectService
  }

  const handleToggleFavorite = async () => {
    await getProjectService().toggleFavoriteProject(
      props.project.id,
      props.project.isFavorite,
    )
    props.onFavoriteChange()
  }

  const handleDownloadAction = async () => {
    let formatsets = null
    const projectService = getProjectService()
    const formatsetsResponse = await projectService.getFormatsets({
      projectId: props.project.id,
      includes: 'creatives',
    })
    if (formatsetsResponse.type === 'success') {
      formatsets = formatsetsResponse.payload
    }

    let projectPreviewlinks: any = null
    const projectResponse = await projectService.getProjectForPreview(
      props.project.id,
    )
    if (projectResponse.type === 'success') {
      projectPreviewlinks = projectResponse.payload
    }
    let expireString = ''
    let excellJsonArray: JsonArrayItemType[] = []
    if (formatsets && projectPreviewlinks) {
      formatsets.forEach((formatset) => {
        formatset.creatives.forEach((creative) => {
          const project = projectPreviewlinks.find(
            (p: any) => p.creative_uuid === creative.id,
          )
          const expire = new Date(project.expire).toLocaleString('de-DE')
          expireString = new Date(project.expire).toLocaleDateString('de-DE')

          const excellRow = {
            Platform: formatset.type,
            Project: props.project.name,
            Formatset: formatset.name,
            Creative: creative.name,
            Status: creative.status.label,
            Previewlink: project.url,
            'Expiration Time': expire,
          }
          excellJsonArray.push(excellRow)
        })
      })
    }

    let ws = utils.json_to_sheet(excellJsonArray)
    const wb = utils.book_new()

    const headerStyle = {
      font: {
        bold: true,
      },
    }

    ws['A1'].s = headerStyle
    ws['B1'].s = headerStyle
    ws['C1'].s = headerStyle
    ws['D1'].s = headerStyle
    ws['E1'].s = headerStyle
    ws['F1'].s = headerStyle
    ws['G1'].s = headerStyle

    ws['A1'].s = headerStyle
    ws['!cols'] = [
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 40 },
      { wch: 20 },
      { wch: 60 },
      { wch: 20 },
    ]

    utils.book_append_sheet(wb, ws, 'Data')
    writeFile(wb, `previewlinks-${props.project.name}-${expireString}.xlsx`)
  }

  const handleCopyAction = async () => {
    const history = DI.get('history')
    const projectService = getProjectService()
    const response = await projectService.copyProject(props.project.id)
    if (response.payload.invalidFormatsets.length > 0) {
      const formatsetsResponse = await projectService.getFormatsets({
        projectId: props.project.id,
      })
      let namesArray: string[] = []
      formatsetsResponse.type === 'success' &&
        formatsetsResponse.payload.forEach((fo) => {
          if (response.payload.invalidFormatsets?.includes(fo.id)) {
            namesArray.push(fo.name)
          }
        })
      props.openInvalidCopyProjectDialog(
        namesArray.join(', '),
        response.payload.data.id,
      )
    } else {
      history.push(`/project/${response.payload.data.id}`)
    }
  }

  const renderActionButton = (action: ProjectAction, i: number) => {
    return (
      <span key={`${action.name}-${i}}`}>
        <Tooltip title={action.title} placement="top">
          <span
            className={`MuiActions-CreativePool cp-cta cp-cta-${action.name}`}>
            <ActionButton action={action} onClick={handleAction} />
          </span>
        </Tooltip>
      </span>
    )
  }

  const editIcon = (
    <span className="MuiActions-CreativePool cp-cta cp-cta-edit-project">
      <ProjectDialog
        project={props.project}
        activator={
          <Tooltip
            title={translateWithUserLocale('projects.edit_project')}
            placement="top">
            <Button>
              <ActionIcon>edit</ActionIcon>
            </Button>
          </Tooltip>
        }
        onSave={props.onProjectSave}
      />
    </span>
  )
  const viewIcon = (
    <span className="MuiActions-CreativePool cp-cta cp-cta-view-project">
      <Tooltip
        title={translateWithUserLocale('projects.view_project')}
        placement="top">
        <Button onClick={handleViewAction}>
          <ActionIcon>visibility</ActionIcon>
        </Button>
      </Tooltip>
    </span>
  )
  const downloadIcon = (
    <span className="MuiActions-CreativePool cp-cta cp-cta-download-preview-links">
      <Tooltip
        title={translateWithUserLocale('projects.download_preview_links')}
        placement="top">
        <Button onClick={handleDownloadAction}>
          <IconContainer>
            <img src={excellFileIcon} alt="" />
          </IconContainer>
        </Button>
      </Tooltip>
    </span>
  )

  const favoriteIcon = (
    <span className="MuiActions-CreativePool cp-cta cp-cta-download-preview-links">
      <Tooltip
        title={
          props.project.isFavorite
            ? translateWithUserLocale('projects.unfavorite_project')
            : translateWithUserLocale('projects.favorite_project')
        }
        placement="top">
        <Button onClick={handleToggleFavorite}>
          <IconContainer>
            <ActionIcon>
              {props.project.isFavorite ? 'star' : 'star_border'}
            </ActionIcon>
          </IconContainer>
        </Button>
      </Tooltip>
    </span>
  )
  const copyIcon = (
    <span className="MuiActions-CreativePool cp-cta cp-cta-download-preview-links">
      <Tooltip
        title={translateWithUserLocale('projects.copy_project')}
        placement="top">
        <Button onClick={handleCopyAction}>
          <IconContainer>
            <ActionIcon>file_copy</ActionIcon>
          </IconContainer>
        </Button>
      </Tooltip>
    </span>
  )

  const possibleActions = props.project.possibleActions
  const canEdit =
    possibleActions.findIndex((action) => action.name === 'edit') !== -1
  const canDownload =
    possibleActions.findIndex((action) => action.name === 'download') !== -1
  const actionsWithoutEditAndView = possibleActions.filter((action) => {
    return (
      action.name !== 'edit' &&
      action.name !== 'view' &&
      action.name !== 'download'
    )
  })

  return (
    <Box
      sx={{
        display: 'flex',
        flexFlow: 'row',
        justifyContent: 'flex-start',
        fontSize: 18,
        color: 'custom.gray.dark',
      }}>
      {viewIcon}
      {canEdit && editIcon}
      {props.canCopy && copyIcon}
      {actionsWithoutEditAndView.map(renderActionButton)}
      {canDownload && downloadIcon}
      {favoriteIcon}
    </Box>
  )
}

export default Actions
